/**
 * SuperAdmin - Otomatik Mail Raporlari Yonetim Sayfasi.
 *
 * Bu sayfaya YALNIZCA rolu "superadmin" olan kullanicilar erisebilir. Erisim guard'i
 * sayfa seviyesinde uygulanir; asil yetki backend'de SuperAdminGuard ile dogrulanir.
 *
 * Sekme yapisi:
 *  1) Sirket Erisim Yonetimi - Tum sirketleri listele, MailReportsEnabled toggle
 *  2) SMTP Ayarlari - Mevcut SmtpSettingsTab componentini yeniden kullan
 *  3) Sablon (Branding) - TemplateSettingsTab componentini yeniden kullan
 *
 * Sayfa, ortak SuperAdminLayout (responsive sidebar + AppBar) icinde render edilir.
 */

import React from 'react';
import { Alert, Box, Button, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../../contexts/AuthContext';
import SuperAdminLayout from '../SuperAdminLayout';
import CompanyAccessTab from './CompanyAccessTab';
import SmtpSettingsTab from '../../accounting/mailReports/tabs/SmtpSettingsTab';
import TemplateSettingsTab from '../../accounting/mailReports/tabs/TemplateSettingsTab';

type TabId = 'access' | 'smtp' | 'template';

interface PanelProps {
  current: TabId;
  value: TabId;
  children: React.ReactNode;
}

const TabPanel: React.FC<PanelProps> = ({ current, value, children }) => (
  <div hidden={current !== value} role="tabpanel" style={{ width: '100%' }}>
    {current === value && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const SuperAdminMailReports: React.FC = () => {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = React.useState<TabId>('access');

  if (!isSuperAdmin) {
    return (
      <SuperAdminLayout title="Mail Raporları" icon={<MarkEmailReadIcon />}>
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={() => navigate('/superadmin')} startIcon={<ArrowBackIcon />}>
              SuperAdmin Ana Sayfa
            </Button>
          }
        >
          Bu sayfaya yalnızca SuperAdmin kullanıcılar erişebilir.
        </Alert>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout title="Mail Raporları" icon={<MarkEmailReadIcon />}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <MarkEmailReadIcon color="primary" fontSize="large" />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h5" fontWeight={600}>
            Otomatik Mail Raporları Yönetimi
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Tüm şirketler için merkezi erişim, SMTP ve şablon yönetimi.
          </Typography>
        </Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/superadmin')}>
          SuperAdmin Ana Sayfa
        </Button>
      </Stack>

      <Paper elevation={0} variant="outlined" sx={{ mt: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Şirket Erişim Yönetimi" value="access" />
          <Tab label="SMTP Ayarları" value="smtp" />
          <Tab label="Şablon / Branding" value="template" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          <TabPanel current={tab} value="access">
            <CompanyAccessTab />
          </TabPanel>
          <TabPanel current={tab} value="smtp">
            <SmtpSettingsTab />
          </TabPanel>
          <TabPanel current={tab} value="template">
            <TemplateSettingsTab />
          </TabPanel>
        </Box>
      </Paper>
    </SuperAdminLayout>
  );
};

export default SuperAdminMailReports;
